import i18n from '@/utils/i18n';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Animated, Easing, Pressable, StyleSheet } from 'react-native';
import { BlurView } from 'expo-blur';
import { OrderEntryMobileHeaderPanelContents } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/components/order-entry-mobile-header-panel-contents';
import { TIMING_PRESETS, View, YStack } from '@usepepper/sprout-kit-ui';

const CONTENT_FADE_DURATION_MS = 260;
const CONTENT_FADE_DELAY_MS = 90;
const BACKDROP_FADE_DURATION_MS = TIMING_PRESETS.sheetSlide.duration;
const FADE_EASING = Easing.bezier(0.25, 0.1, 0.25, 1);

// Fallback sheet height while we wait for the onLayout measurement.
// Using this prevents the animation from flickering when we go from unmeasured to measured.
const UNMEASURED_SHEET_HEIGHT = 600;

type PanelPhase = 'closed' | 'closing' | 'open' | 'opening';

interface OrderEntryMobileHeaderPanelProps {
  details: React.ReactNode;
  isOpen: boolean;
  onClose: () => void;
  onSelectRestaurant: () => void;
  restaurantName: string;
  restaurantUUID: string;
}

export const OrderEntryMobileHeaderPanel: React.FC<
  OrderEntryMobileHeaderPanelProps
> = ({
  details,
  isOpen,
  onClose,
  onSelectRestaurant,
  restaurantName,
  restaurantUUID,
}) => {
  const [sheetHeight, setSheetHeight] = useState(0);
  const [phase, setPhase] = useState<PanelPhase>(isOpen ? 'opening' : 'closed');

  const slide = useRef(new Animated.Value(0)).current;
  const contentFade = useRef(new Animated.Value(0)).current;
  const backdropFade = useRef(new Animated.Value(0)).current;

  // Measures the height of the sheet content, so we know how large to animate it open
  const handleSheetLayout = useCallback(
    ({ nativeEvent }: { nativeEvent: { layout: { height: number } } }) => {
      const measured = Math.round(nativeEvent.layout.height);
      setSheetHeight((previous) =>
        previous === measured ? previous : measured,
      );
    },
    [],
  );

  useEffect(() => {
    setPhase((current) => {
      if (isOpen) {
        return current === 'open' ? 'open' : 'opening';
      }
      return current === 'closed' ? 'closed' : 'closing';
    });
  }, [isOpen]);

  useEffect(() => {
    if (phase !== 'opening' && phase !== 'closing') {
      return;
    }

    const isOpening = phase === 'opening';
    const toValue = isOpening ? 1 : 0;
    const animation = Animated.parallel([
      Animated.timing(slide, {
        ...TIMING_PRESETS.sheetSlide,
        toValue,
        useNativeDriver: true,
      }),
      Animated.timing(contentFade, {
        delay: isOpening ? CONTENT_FADE_DELAY_MS : 0,
        duration: CONTENT_FADE_DURATION_MS,
        easing: FADE_EASING,
        toValue,
        useNativeDriver: true,
      }),
      Animated.timing(backdropFade, {
        duration: BACKDROP_FADE_DURATION_MS,
        easing: FADE_EASING,
        toValue,
        useNativeDriver: true,
      }),
    ]);

    animation.start(({ finished }) => {
      if (finished) {
        setPhase(isOpening ? 'open' : 'closed');
      }
    });

    return () => animation.stop();

    // `sheetHeight` is a dependency so the first open restarts against the real
    // travel distance rather than swapping the interpolation mid-flight
  }, [contentFade, phase, backdropFade, sheetHeight, slide]);

  // Checks to make sure the panel is fully open before allowing taps on the backdrop.
  // Otherwise a tap to open the panel will also register as a tap on the backdrop, closing it.
  const handleBackdropPress = () => {
    if (phase === 'open') {
      onClose();
    }
  };

  // Wait for the panel to fully animate closed before rendering nothing
  if (phase === 'closed' && !isOpen) {
    return null;
  }

  return (
    <YStack accessibilityViewIsModal aria-modal flex={1} overflow="hidden">
      <Animated.View style={[styles.backdrop, { opacity: backdropFade }]}>
        <Pressable
          accessibilityLabel={i18n.t(
            'sales_portal.order_entry.mobile_header.a11y.backdrop',
          )}
          onPress={handleBackdropPress}
          style={StyleSheet.absoluteFill}
        >
          <BlurView
            experimentalBlurMethod="dimezisBlurView"
            intensity={15}
            style={StyleSheet.absoluteFill}
            tint="light"
          />
          <View backgroundColor="$transparentLight50" flex={1} />
        </Pressable>
      </Animated.View>
      <Animated.View
        onLayout={handleSheetLayout}
        style={[
          styles.sheetAnchor,
          {
            transform: [
              {
                translateY: slide.interpolate({
                  inputRange: [0, 1],
                  outputRange: [-(sheetHeight || UNMEASURED_SHEET_HEIGHT), 0],
                }),
              },
            ],
          },
        ]}
      >
        <OrderEntryMobileHeaderPanelContents
          contentOpacity={contentFade}
          details={details}
          onClose={onClose}
          onSelectRestaurant={onSelectRestaurant}
          restaurantName={restaurantName}
          restaurantUUID={restaurantUUID}
        />
      </Animated.View>
    </YStack>
  );
};

const styles = StyleSheet.create({
  backdrop: {
    ...StyleSheet.absoluteFillObject,
  },
  sheetAnchor: {
    left: 0,
    position: 'absolute',
    right: 0,
    top: 0,
  },
});
